<template>
  <div id="info-tab" class="tab is-full-height">
    <div class="label">{{ bidPackageId ? 'Bid Package' : 'Project' }} Info</div>

    <div class="info is-flex is-full-height">
      <div class="column has-margin-right is-half-desktop is-half-widescreen is-one-third-fullhd is-full-tablet is-full-mobile overflow-scroll">
        <o-field class="row" label="Project #" horizontal>{{project?.number}}</o-field>

        <o-field class="row" :label="`${bidPackageId ? 'Bid Package' : 'Project'} Name`" horizontal>{{ bidPackageId ? bidPackage?.name : project?.name }}</o-field>

        <o-field class="row" label="Invited" horizontal>{{ project?.invited ? 'Yes' : 'No' }}</o-field>

        <o-field class="row"  label="Location" horizontal>{{ project?.location }}</o-field>

        <o-field class="row" label="Address" horizontal>
          <div>
            <div>{{ project?.address }}</div>
            <div v-if="mapLink" class="map-link"><a :href="mapLink" target="_blank"><span><o-icon icon="location-dot" class="is-small" pack="fas" /></span> Map It</a></div>
            <div v-if="mapSrc">
              <iframe width="200"
                      height="400"
                      style="border:0"
                      class="map-iframe"
                      :src="mapSrc"
                      allowfullscreen
              />
            </div>
          </div>
        </o-field>

        <o-field class="row"  label="City" horizontal>{{ project?.city }}</o-field>

        <o-field class="row" label="State" horizontal>{{ project?.state }}</o-field>

        <o-field class="row"  label="Zip" horizontal>{{ project?.zip }}</o-field>

        <o-field v-if="schedule?.sub_bid_date" class="row" label="Bid Date" horizontal>
          {{ formatRFQDate(schedule.sub_bid_date) }}
        </o-field>

        <o-field v-if="schedule?.sub_bid_time" class="row" label="Bid Time" horizontal>
          {{ formatRFQDateTime('', schedule.sub_bid_time) }}
        </o-field>

        <o-field v-if="schedule?.rfi_due_date" class="row" label="RFI Due Date" horizontal>
          {{ formatRFQDate(schedule.rfi_due_date) }}
        </o-field>

        <o-field v-if="schedule?.rfi_due_time" class="row" label="RFI Due Time" horizontal>
          {{ formatRFQDateTime('', schedule.rfi_due_time) }}
        </o-field>

        <o-field v-if="schedule?.site_meeting_date" class="row" label="Site Meeting Date" horizontal>
          {{ formatRFQDate(schedule.site_meeting_date) }}
        </o-field>

        <o-field v-if="schedule?.site_meeting_time" class="row" label="Site Meeting Time" horizontal>
          {{ formatRFQDateTime('', schedule.site_meeting_time) }}
        </o-field>

      </div>

      <div class="column is-half-desktop is-half-widescreen is-two-thirds-fullhd is-full-tablet is-full-mobile overflow-scroll">
        <o-field class="row" label="Scope" horizontal><HTMLText :value="bidPackageId ? bidPackage?.scope : project?.scope" /></o-field>

        <o-field class="row" label="Plans" horizontal><HTMLText :value="bidPackageId ? bidPackage?.exclusions : project?.exclusions" /></o-field>

        <o-field class="row" label="Other Info" horizontal><HTMLText :value="bidPackageId ? bidPackage?.other_info : project?.other_info" /></o-field>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import {computed, toRefs} from "vue";
import {storeToRefs} from "pinia";
import HTMLText from "@/components/HTMLText.vue";
import {usePublicProjectStore} from "@/stores/PublicProjectStore";
import {useProjectStore} from "@/stores/ProjectStore";
import {useBidPackageStore} from "@/stores/BidPackageStore";
import formatRFQDate from '@/use/formatRFQDate';
import formatRFQDateTime from '@/use/formatRFQDateTime';

const projectStore = useProjectStore();
const publicProjectStore = usePublicProjectStore();
const { bidPackage } = storeToRefs(useBidPackageStore());

const props = withDefaults(defineProps<{
  bidPackageId: number | null,
  publicProjectView?: boolean,
}>(), {
  bidPackageId: null,
  publicProjectView: false,
});
const {
  bidPackageId,
  publicProjectView,
} = toRefs(props);

const fullAddress = computed(() => `${project.value?.address},${project.value?.city},${project.value?.state},${project.value?.zip}`);

const hasAddress = computed(() => project.value?.address || project.value?.city || project.value?.state || project.value?.zip);

const mapSrc = computed(() => {
  if (hasAddress.value) {
    const q = encodeURI(fullAddress.value);
    const key = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
    return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${q}&zoom=15`;
  }

  return '';
});

const mapLink = computed(() => {
  if (hasAddress.value){
    let url = "https://maps.google.com/maps?q=";
    url += `${project.value?.address},+${project.value?.city},+${project.value?.state}`;
    url += "&hl=en&z=16";
    return url;
  }

  return '';
});

const project = computed(() => publicProjectView.value ? publicProjectStore.project : projectStore.project);
const schedule = computed(() => bidPackageId.value ? bidPackage.value : project.value);

</script>

<style lang="scss" scoped>

#info-tab {
  .field {
    padding: 3px;

    &.row {
      // border-top:1px solid rgb(from $grey-darker r g b / 0.05);
    }

    &:not(:last-child) {
      margin-bottom: .5rem !important;
    }
  }

  .field-label {
    .label {
      min-width: 200px;
    }
  }

  .info {
    .column {
      white-space: break-spaces;
    }

    .overflow-scroll {
      overflow-x: auto;
    }

    .map-iframe {
      border-radius: 5px;
      height: 225px;
    }
  }
}

.map-link{
  position:relative;
  left:-6em;
  font-size:.9em;
}

@media (max-width: 1023px) {
  #info-tab {
    .info {
      flex-direction: column;
    }
  }
}

</style>

<style lang="scss">

#info-tab {
  .field-label {
    .label {
      width: 150px;
      text-wrap: wrap;
      margin-top: 2px !important;
    }
  }

}

</style>
